import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { IconArrowDown, IconArrowUp, IconTrash } from "./icons";

/**
 * The email itself, as the editing surface.
 *
 * It is the same HTML subscribers receive — rendered with `editable`, which
 * only wraps each block in an element carrying its id. A click on the email
 * selects the block under it; a tile dragged from the panel drops between
 * blocks wherever the line shows. The selected block carries its own small
 * toolbar — move up, move down, remove — and Delete removes it too.
 *
 * Security: the frame is sandboxed with `allow-same-origin` and WITHOUT
 * `allow-scripts`. Nothing inside the email can run; the editor reaches into
 * the frame from outside to listen for clicks and drops. Granting both would
 * let author-supplied markup act as the app, so `allow-scripts` must never be
 * added here.
 */
export function EmailCanvas({
  html,
  selectedId,
  dragging,
  onSelect,
  onDrop,
  onMove,
  onRemove,
  locked = false,
}: {
  html: string;
  /** A newsletter past draft: blocks can be selected and read, not changed. */
  locked?: boolean;
  selectedId: string | null;
  /** True while a tile from the panel is being dragged. */
  dragging: boolean;
  onSelect: (id: string | null) => void;
  /** Index among the email's blocks to insert at. */
  onDrop: (index: number) => void;
  onMove: (id: string, delta: -1 | 1) => void;
  onRemove: (id: string) => void;
}) {
  const frameRef = useRef<HTMLIFrameElement>(null);
  const [height, setHeight] = useState(600);
  // Listeners attach once per document; they read the latest props from here.
  const latest = useRef({ onSelect, onDrop, onRemove, dragging, selectedId, locked });
  latest.current = { onSelect, onDrop, onRemove, dragging, selectedId, locked };
  // Where the selected block sits, for its toolbar; null when nothing is.
  const [tools, setTools] = useState<{ top: number; left: number; first: boolean; last: boolean } | null>(null);

  useEffect(() => {
    const frame = frameRef.current;
    if (!frame) return;

    const wire = () => {
      const doc = frame.contentDocument;
      if (!doc?.body) return;

      const style = doc.createElement("style");
      style.textContent = canvasCss();
      doc.head.appendChild(style);

      const indicator = doc.createElement("div");
      indicator.className = "altibbi-editor-drop";
      doc.body.appendChild(indicator);

      const blocks = () => [...doc.querySelectorAll<HTMLElement>("[data-section-id]")];
      // Not `instanceof Element`: nodes in the frame belong to the frame's own
      // window, so the app window's Element never matches them and every
      // click would select nothing.
      const blockOf = (t: EventTarget | null) => {
        const el = t as Element | null;
        return el && typeof el.closest === "function"
          ? el.closest<HTMLElement>("[data-section-id]")
          : null;
      };

      const indexAt = (y: number) => {
        const list = blocks();
        for (let i = 0; i < list.length; i++) {
          const r = list[i].getBoundingClientRect();
          if (y < r.top + r.height / 2) return i;
        }
        return list.length;
      };
      const showIndicator = (index: number) => {
        const list = blocks();
        const scrollY = doc.defaultView?.scrollY ?? 0;
        let top: number;
        if (list.length === 0) top = 40;
        else if (index < list.length) top = list[index].getBoundingClientRect().top + scrollY - 2;
        else top = list[list.length - 1].getBoundingClientRect().bottom + scrollY + 2;
        const ref = list[0]?.getBoundingClientRect();
        indicator.style.top = `${top}px`;
        indicator.style.left = `${(ref?.left ?? 24)}px`;
        indicator.style.width = `${ref?.width ?? 400}px`;
        indicator.dataset.on = "true";
      };

      doc.addEventListener("click", (e) => {
        // A click here is a choice of block, never a visit to its link.
        e.preventDefault();
        const block = blockOf(e.target);
        latest.current.onSelect(block?.dataset.sectionId ?? null);
      });
      // A click on the email moves focus into the frame, so Delete has to be
      // heard here as well as in the app.
      doc.addEventListener("keydown", (e) => {
        const id = latest.current.selectedId;
        if (!id) return;
        if ((e.key === "Delete" || e.key === "Backspace") && !latest.current.locked) {
          e.preventDefault();
          latest.current.onRemove(id);
        } else if (e.key === "Escape") {
          latest.current.onSelect(null);
        }
      });
      doc.addEventListener("mouseover", (e) => {
        doc.querySelectorAll("[data-hover]").forEach((n) => n.removeAttribute("data-hover"));
        blockOf(e.target)?.setAttribute("data-hover", "true");
      });
      doc.addEventListener("mouseleave", () => {
        doc.querySelectorAll("[data-hover]").forEach((n) => n.removeAttribute("data-hover"));
      });
      doc.addEventListener("dragover", (e) => {
        if (!latest.current.dragging) return;
        e.preventDefault();
        if (e.dataTransfer) e.dataTransfer.dropEffect = "copy";
        showIndicator(indexAt(e.clientY));
      });
      doc.addEventListener("dragleave", (e) => {
        if (e.relatedTarget === null) indicator.dataset.on = "false";
      });
      doc.addEventListener("drop", (e) => {
        if (!latest.current.dragging) return;
        e.preventDefault();
        indicator.dataset.on = "false";
        latest.current.onDrop(indexAt(e.clientY));
      });

      const fit = () => setHeight(contentHeight(doc));
      fit();
      // Images arrive after the document; the frame grows with them.
      doc.querySelectorAll("img").forEach((img) => img.addEventListener("load", fit));
    };

    frame.addEventListener("load", wire);
    return () => frame.removeEventListener("load", wire);
  }, [html]);

  // The email reflows when the canvas changes width — a window resize, the
  // panel stacking, Desktop/Mobile — and nothing in React re-renders for that.
  // Re-measure then, so the frame fits and the toolbar stays on its block.
  const [, setReflow] = useState(0);
  useEffect(() => {
    const frame = frameRef.current;
    if (!frame) return;
    let timer = 0;
    const ro = new ResizeObserver(() => {
      // The frame's own document takes its new width a moment after the
      // app's layout, so read it shortly after rather than with stale boxes.
      // A timer, not an animation frame: those pause in hidden tabs.
      window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        const doc = frame.contentDocument;
        if (doc?.body) setHeight(contentHeight(doc));
        setReflow((n) => n + 1);
      }, 50);
    });
    ro.observe(frame);
    return () => { ro.disconnect(); window.clearTimeout(timer); };
  }, []);

  // Selection is drawn inside the frame, so it follows the block wherever it
  // sits rather than an overlay that would drift as images load. The toolbar
  // is the app's own markup (the frame runs no scripts), placed over the
  // block's top edge from the block's measured box.
  useLayoutEffect(() => {
    const frame = frameRef.current;
    const doc = frame?.contentDocument;
    if (!frame || !doc) return;
    doc.querySelectorAll("[data-selected]").forEach((n) => n.removeAttribute("data-selected"));
    const el = selectedId
      ? doc.querySelector<HTMLElement>(`[data-section-id="${CSS.escape(selectedId)}"]`)
      : null;
    if (!el) { setTools(null); return; }
    el.setAttribute("data-selected", "true");
    const all = [...doc.querySelectorAll("[data-section-id]")];
    const r = el.getBoundingClientRect();
    const next = {
      top: Math.max(frame.offsetTop + 6, frame.offsetTop + r.top - 18),
      left: frame.offsetLeft + r.right,
      first: all[0] === el,
      last: all[all.length - 1] === el,
    };
    setTools((prev) =>
      prev && prev.top === next.top && prev.left === next.left && prev.first === next.first && prev.last === next.last
        ? prev : next);
  });

  return (
    <div className="nl-canvas-stage">
      <iframe
        ref={frameRef}
        className="nl-canvas-frame"
        title="Newsletter canvas — click a block to edit it"
        srcDoc={html}
        sandbox="allow-same-origin"
        style={{ height }}
      />
      {tools && selectedId && !locked && (
        <div className="nl-blocktools" role="toolbar" aria-label="Selected block"
          style={{ top: tools.top, left: tools.left }}>
          <button type="button" aria-label="Move block up" title="Move up"
            disabled={tools.first} onClick={() => onMove(selectedId, -1)}>
            <IconArrowUp />
          </button>
          <button type="button" aria-label="Move block down" title="Move down"
            disabled={tools.last} onClick={() => onMove(selectedId, 1)}>
            <IconArrowDown />
          </button>
          <span className="nl-blocktools-sep" aria-hidden />
          <button type="button" className="is-danger" aria-label="Remove block" title="Remove (Delete)"
            onClick={() => onRemove(selectedId)}>
            <IconTrash />
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * The email's own height. The document's scrollHeight never drops below the
 * frame's current height, so a frame sized for a long layout would never
 * shrink back; the body's box is the content alone.
 */
function contentHeight(doc: Document): number {
  return Math.max(480, Math.ceil(doc.body.getBoundingClientRect().height));
}

/**
 * The frame is its own document, so the app's CSS variables do not reach into
 * it. Rather than type hex codes here (the tool standard forbids raw colours
 * in components), read the tokens off the app and hand them over.
 */
function canvasCss(): string {
  const root = getComputedStyle(document.documentElement);
  const t = (name: string) => root.getPropertyValue(name).trim();
  return `
  :root { --ed-brand: ${t("--email-brand")}; --ed-soft: ${t("--email-brand-100")}; --ed-ink3: ${t("--email-ink-3")}; }
  [data-section-id] { position: relative; cursor: pointer; border-radius: 10px;
    outline: 2px solid transparent; outline-offset: 4px;
    transition: outline-color ${t("--t-fast")} ${t("--ease")}; }
  [data-section-id][data-hover] { outline-color: var(--ed-soft); }
  [data-section-id][data-selected] { outline-color: var(--ed-brand); }
  .altibbi-editor-empty { margin: 8px 0; padding: 20px 16px; border: 1.5px dashed var(--ed-soft);
    border-radius: 12px; color: var(--ed-ink3); text-align: center;
    font: 500 13px/20px 'IBM Plex Sans Arabic', system-ui, sans-serif; }
  .altibbi-editor-drop { position: absolute; height: 3px; border-radius: 999px;
    background: var(--ed-brand); pointer-events: none; display: none; z-index: 10; }
  .altibbi-editor-drop[data-on="true"] { display: block; }
  a { cursor: pointer; }
`;
}
